import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  FaUserCircle,
  FaBell,
  FaTimes,
  FaCheck,
  FaRss,
  FaHome,
  FaStore,
  FaSun,
  FaMoon,
  FaDesktop,
  FaSearch,
  FaArrowLeft,
  FaUserFriends,
} from "react-icons/fa";
import { AiFillMessage } from "react-icons/ai";
import SearchBar from "../filters/SearchBar";
import Logo from "../common/Logo";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { usePublicPages } from "../../contexts/PublicPagesContext";
import { useToast } from "../../contexts/ToastContext";
import { useSocket } from "../../contexts/SocketContext";
import { useTheme } from "../../contexts/ThemeContext";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import CategoryNav from "./CategoryNav";
import Loader from "../common/Loader";
import { getOwnProfilePath } from "../../utils/profilePaths";
import { useTopBarSlots } from "../../contexts/TopBarSlotsContext";
import { resolveNotificationNavigation } from "../../utils/notificationNavigation";
import { resolveMessageNavigation } from "../../utils/messageNavigation";
import styles from "../../styles/components/layout/TopBar.module.css";

// Types that belong exclusively to the Messages feed — must never appear in Notifications.
const MESSAGE_NOTIFICATION_TYPES = new Set(["message", "marketplace_message"]);

const isFollowRequestNotification = (notification) =>
  notification?.type === "follow-request" ||
  notification?.title === "New Follow Request";

const resolveFollowRequestId = (notification, followRequests) => {
  if (!followRequests?.length) return null;

  const pendingById = new Map(
    followRequests.map((request) => [String(request._id), request]),
  );

  const storedId = notification?.data?.requestId;
  if (storedId && pendingById.has(String(storedId))) {
    return String(storedId);
  }

  const match = notification?.message?.match(/^(.+)\s+wants to follow you$/i);
  if (!match) return null;

  const name = match[1].trim().toLowerCase();
  const found = followRequests.find((request) => {
    const requester = request.requester;
    if (!requester) return false;
    const fullName = `${requester.firstName || ""} ${requester.lastName || ""}`
      .trim()
      .toLowerCase();
    const username = (requester.username || "").toLowerCase();
    return fullName === name || username === name;
  });

  return found ? String(found._id) : null;
};

const prepareNotificationList = (list, followRequests) => {
  const seenRequestIds = new Set();

  return list.filter((notification) => {
    if (!isFollowRequestNotification(notification)) return true;

    const requestId = resolveFollowRequestId(notification, followRequests);
    if (!requestId) return false;

    if (seenRequestIds.has(requestId)) return false;
    seenRequestIds.add(requestId);
    return true;
  });
};

const pruneFollowRequestNotifications = (notifications, followRequests) =>
  notifications.filter((notification) => {
    if (!isFollowRequestNotification(notification)) return true;
    return resolveFollowRequestId(notification, followRequests) !== null;
  });

const parseSenderNameFromMessage = (text) => {
  const match = text?.match(/^(.+?)\s+sent you a message$/i);
  return match ? match[1].trim() : null;
};

const THEME_OPTIONS = [
  { value: "system", label: "System", Icon: FaDesktop },
  { value: "light", label: "Light", Icon: FaSun },
  { value: "dark", label: "Dark", Icon: FaMoon },
];

const TopBar = ({ variant = "default" }) => {
  const { user, logout, userAuthChecked } = useUserAuth();
  const { isPageEnabled } = usePublicPages();
  const { showToast } = useToast();
  const { theme, setTheme } = useTheme();
  const userProfilePath = getOwnProfilePath(user);
  const messagesPath = getOwnProfilePath(user, "/messages");
  const notificationsPath = getOwnProfilePath(user, "/notifications");
  const settingsPath = getOwnProfilePath(user, "/settings");
  const { socket, isConnected } = useSocket();
  const { setLeftSlotEl, leftSlotOccupied } = useTopBarSlots();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMessagesDropdown, setShowMessagesDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [followRequests, setFollowRequests] = useState([]);
  const [acceptingRequestId, setAcceptingRequestId] = useState(null);
  const dropdownRef = useRef(null);
  const messagesDropdownRef = useRef(null);
  const profileMenuWrapperRef = useRef(null);
  const profileMenuCloseTimerRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get("/users/me/notifications/unread-count");
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      // Silently fail - user might not be logged in
      setUnreadCount(0);
    }
  }, []);

  const fetchUnreadMessagesCount = useCallback(async () => {
    try {
      const [directRes, systemRes] = await Promise.allSettled([
        api.get("/users/me/notifications/messages/unread-count"),
        api.get("/users/me/system-messages/unread-count"),
      ]);

      const directCount =
        directRes.status === "fulfilled" ? directRes.value.data.count || 0 : 0;
      const systemCount =
        systemRes.status === "fulfilled" ? systemRes.value.data.count || 0 : 0;

      setUnreadMessagesCount(directCount + systemCount);
    } catch (err) {
      // Silently fail - user might not be logged in
      setUnreadMessagesCount(0);
    }
  }, []);

  const fetchRecentNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const [notificationsRes, followRequestsRes] = await Promise.allSettled([
        api.get("/users/me/notifications"),
        api.get("/users/me/follow-requests"),
      ]);

      const list =
        notificationsRes.status === "fulfilled"
          ? Array.isArray(notificationsRes.value.data)
            ? notificationsRes.value.data
            : notificationsRes.value.data.notifications || []
          : [];

      const requests =
        followRequestsRes.status === "fulfilled"
          ? followRequestsRes.value.data.requests || []
          : [];

      // Client-side safety: strip any message-type items that shouldn't appear here
      const notificationsOnly = list.filter(
        (n) => !MESSAGE_NOTIFICATION_TYPES.has(n.type),
      );

      const actionable = prepareNotificationList(notificationsOnly, requests);

      // Remove stale follow-request notifications that were already handled
      notificationsOnly
        .filter(
          (notification) =>
            isFollowRequestNotification(notification) &&
            !resolveFollowRequestId(notification, requests),
        )
        .forEach((notification) => {
          const id = notification._id || notification.id;
          if (id) {
            api.delete(`/users/me/notifications/${id}`).catch(() => {});
          }
        });

      setFollowRequests(requests);
      setNotifications(actionable.slice(0, 5));
    } catch (err) {
      setNotifications([]);
      setFollowRequests([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const fetchRecentMessages = useCallback(async () => {
    try {
      setLoadingMessages(true);
      const [directRes, systemRes] = await Promise.allSettled([
        api.get("/users/me/notifications/messages"),
        api.get("/users/me/system-messages", { params: { page: 1, limit: 5 } }),
      ]);

      const directList =
        directRes.status === "fulfilled"
          ? Array.isArray(directRes.value.data)
            ? directRes.value.data
            : directRes.value.data.notifications || []
          : [];

      const systemListRaw =
        systemRes.status === "fulfilled"
          ? systemRes.value.data.messages || systemRes.value.data || []
          : [];

      const systemList = systemListRaw.map((delivery) => ({
        _id: delivery._id,
        read: delivery.read,
        createdAt: delivery.createdAt,
        title: delivery.systemMessage?.subject || "System message",
        message: delivery.systemMessage?.body || "",
        url: messagesPath,
        isSystem: true,
      }));

      const combined = [...directList, ...systemList].sort((a, b) => {
        const aDate = new Date(a.createdAt || a.date || 0).getTime();
        const bDate = new Date(b.createdAt || b.date || 0).getTime();
        return bDate - aDate;
      });

      setMessages(combined.slice(0, 5));
    } catch (err) {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [messagesPath]);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchUnreadMessagesCount();
    } else {
      setUnreadCount(0);
      setUnreadMessagesCount(0);
      setNotifications([]);
      setMessages([]);
    }
  }, [user, fetchUnreadCount, fetchUnreadMessagesCount]);

  // Fallback polling only when socket is unavailable.
  useEffect(() => {
    if (!user || isConnected) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchUnreadMessagesCount();
    }, 60000);

    return () => clearInterval(interval);
  }, [user, isConnected, fetchUnreadCount, fetchUnreadMessagesCount]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (
        messagesDropdownRef.current &&
        !messagesDropdownRef.current.contains(event.target)
      ) {
        setShowMessagesDropdown(false);
      }
      if (
        profileMenuWrapperRef.current &&
        !profileMenuWrapperRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };
    if (showDropdown || showProfileMenu || showMessagesDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown, showProfileMenu, showMessagesDropdown]);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 769px)");
    const closeOnDesktop = (event) => {
      if (event.matches) setMobileSearchOpen(false);
    };
    media.addEventListener("change", closeOnDesktop);
    return () => media.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!mobileSearchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileSearchOpen]);

  const openProfileMenu = () => {
    if (profileMenuCloseTimerRef.current) {
      clearTimeout(profileMenuCloseTimerRef.current);
      profileMenuCloseTimerRef.current = null;
    }
    setShowProfileMenu(true);
  };

  const closeProfileMenu = () => {
    profileMenuCloseTimerRef.current = setTimeout(() => {
      setShowProfileMenu(false);
    }, 150);
  };

  const handleBellClick = (e) => {
    e.preventDefault();
    if (!showDropdown) {
      fetchRecentNotifications();
    }
    setShowDropdown(!showDropdown);
    setShowMessagesDropdown(false); // Close messages dropdown if open
  };

  const handleMessagesClick = (e) => {
    e.preventDefault();
    if (!showMessagesDropdown) {
      fetchRecentMessages();
    }
    setShowMessagesDropdown(!showMessagesDropdown);
    setShowDropdown(false); // Close notifications dropdown if open
  };

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/users/me/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => ((n._id || n.id) === id ? { ...n, read: true } : n)),
      );
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/users/me/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => (n._id || n.id) !== id));
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const handleAcceptFollowRequest = async (requestId, e) => {
    e.stopPropagation();
    if (!requestId || acceptingRequestId) return;

    const remainingRequests = followRequests.filter(
      (request) => String(request._id) !== String(requestId),
    );

    const clearAcceptedNotifications = () => {
      setFollowRequests(remainingRequests);
      setNotifications((prev) =>
        pruneFollowRequestNotifications(prev, remainingRequests),
      );
      fetchUnreadCount();
    };

    try {
      setAcceptingRequestId(requestId);
      await api.post(`/users/me/follow-requests/${requestId}/accept`);
      clearAcceptedNotifications();
    } catch (err) {
      console.error("Failed to accept follow request:", err);

      // Request already handled — drop stale notifications from the dropdown
      if (err.response?.status === 404) {
        clearAcceptedNotifications();
        showToast({
          message: "Follow request already handled",
          type: "info",
        });
        return;
      }

      showToast({
        message:
          err.response?.data?.message || "Failed to accept follow request",
        type: "error",
      });
    } finally {
      setAcceptingRequestId(null);
    }
  };

  const handleOpenNotification = async (notification, e) => {
    e.stopPropagation();
    const id = notification._id || notification.id;

    if (notification && !notification.read) {
      try {
        await api.post(`/users/me/notifications/${id}/read`, {});
        setNotifications((prev) =>
          prev.map((n) => ((n._id || n.id) === id ? { ...n, read: true } : n)),
        );
        fetchUnreadCount();
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }

    setShowDropdown(false);

    const destination = resolveNotificationNavigation(notification, user, {
      notificationsPath,
      messagesPath,
    });
    navigate(
      { pathname: destination.pathname, hash: destination.hash },
      { state: destination.state },
    );
  };

  const resolveMessageSender = async (message) => {
    const senderId = message?.data?.senderId;
    if (senderId) {
      return { _id: senderId };
    }

    const senderName = parseSenderNameFromMessage(message?.message);
    if (!senderName) return null;

    try {
      const res = await api.get("/messages/conversations", {
        params: { scope: "community" },
      });
      const conversations = res.data.conversations || [];
      const match = conversations.find((conv) => {
        const fullName = `${conv.otherUser?.firstName || ""} ${conv.otherUser?.lastName || ""}`
          .trim();
        return fullName === senderName;
      });
      return match?.otherUser || null;
    } catch (err) {
      console.error("Failed to resolve message sender:", err);
      return null;
    }
  };

  const handleOpenMessage = async (message, e) => {
    e.stopPropagation();
    const id = message?._id || message?.id;

    if (message && !message.read) {
      const isSystem = Boolean(message?.isSystem || message?.systemMessage);
      try {
        const endpoint = isSystem
          ? `/users/me/system-messages/${id}/read`
          : `/users/me/notifications/${id}/read`;
        await api.post(endpoint, {});
        setMessages((prev) =>
          prev.map((m) => ((m._id || m.id) === id ? { ...m, read: true } : m)),
        );
        fetchUnreadMessagesCount();
      } catch (err) {
        console.error("Failed to mark message as read:", err);
      }
    }

    setShowMessagesDropdown(false);

    const destination = resolveMessageNavigation(message, user, { messagesPath });

    if (
      !destination.state.openSystemThread &&
      !destination.state.conversationId &&
      !destination.state.startConversationWith
    ) {
      const sender = await resolveMessageSender(message);
      if (sender) {
        destination.state = {
          ...destination.state,
          startConversationWith: sender,
        };
      }
    }

    navigate(destination.pathname, { state: destination.state });
  };

  const handleMarkReadMessage = async (message, e) => {
    e.stopPropagation();
    const id = message?._id || message?.id;
    if (!id) return;
    const isSystem = Boolean(message?.isSystem || message?.systemMessage);
    try {
      const endpoint = isSystem
        ? `/users/me/system-messages/${id}/read`
        : `/users/me/notifications/${id}/read`;
      await api.post(endpoint, {});
      setMessages((prev) =>
        prev.map((m) => ((m._id || m.id) === id ? { ...m, read: true } : m)),
      );
      fetchUnreadMessagesCount();
    } catch (err) {
      console.error("Failed to mark message as read:", err);
    }
  };

  const handleDeleteMessage = async (message, e) => {
    e.stopPropagation();
    if (message?.isSystem || message?.systemMessage) return;
    const id = message?._id || message?.id;
    if (!id) return;
    try {
      await api.delete(`/users/me/notifications/${id}`);
      setMessages((prev) => prev.filter((m) => (m._id || m.id) !== id));
      fetchUnreadMessagesCount();
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  };

  // Listen for realtime unread updates via socket.
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewMessage = () => {
      fetchUnreadMessagesCount();
      if (showMessagesDropdown) {
        fetchRecentMessages();
      }
    };

    const handleNewNotification = () => {
      fetchUnreadCount();
      if (showDropdown) {
        fetchRecentNotifications();
      }
    };

    socket.on("new_message", handleNewMessage);
    socket.on("new_system_message", handleNewMessage);
    socket.on("new_notification", handleNewNotification);

    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("new_system_message", handleNewMessage);
      socket.off("new_notification", handleNewNotification);
    };
  }, [
    socket,
    isConnected,
    showDropdown,
    showMessagesDropdown,
    fetchRecentMessages,
    fetchUnreadMessagesCount,
    fetchUnreadCount,
    fetchRecentNotifications,
  ]);

  // Refresh message count when navigating to messages page
  useEffect(() => {
    if (
      location.pathname.endsWith("/messages")
    ) {
      // Small delay to ensure backend has processed the read status
      setTimeout(() => {
        fetchUnreadMessagesCount();
      }, 500);
    }
  }, [location.pathname, fetchUnreadMessagesCount]);

  // Refresh message count when window regains focus (in case messages were viewed in another tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchUnreadMessagesCount();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchUnreadMessagesCount]);

  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await logout();
      setShowProfileMenu(false);
      navigate("/");
    } catch (err) {
      // Silent fail – logout errors are handled in auth context if needed
    }
  };

  const formatTime = (dateString) => {
    try {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  const stripNavClass = ({ isActive }) =>
    isActive
      ? `${styles.stripNavLink} ${styles.stripNavLinkActive}`
      : styles.stripNavLink;

  return (
    <header
      className={`${styles.siteHeader} ${
        mobileSearchOpen ? styles.searchOpen : ""
      }`}
    >
      <div
        className={`${styles.topbarContainer} ${
          variant === "compact" ? styles.topbarContainerCompact : ""
        }`}
      >
        <div
          className={`${styles.topbarContainerInner} ${
            leftSlotOccupied ? styles.topbarContainerInnerWithInset : ""
          }`}
        >
          {!leftSlotOccupied && (
          <div className={styles.logoNavCluster}>
            <CategoryNav variant="mobile" />
            <Link to="/" className={styles.logoLink}>
              <Logo />
            </Link>

            <nav className={styles.stripNav} aria-label="Main">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `${stripNavClass({ isActive })} ${styles.stripNavLinkLabeled}`
                }
                end
                aria-label="Home"
                title="Home"
              >
                <FaHome size={20} />
                <span className={styles.stripNavLabel}>Home</span>
              </NavLink>
              <CategoryNav />
              {isPageEnabled("feed") && (
                <NavLink
                  to="/community"
                  className={({ isActive }) =>
                    `${stripNavClass({ isActive })} ${styles.stripNavLinkLabeled}`
                  }
                  end
                  aria-label="Community"
                  title="Community"
                >
                  <FaRss size={20} />
                  <span className={styles.stripNavLabel}>Community</span>
                </NavLink>
              )}
              {isPageEnabled("marketplace") && (
                <NavLink
                  to="/marketplace"
                  className={({ isActive }) =>
                    `${stripNavClass({ isActive })} ${styles.stripNavLinkLabeled}`
                  }
                  aria-label="Marketplace"
                  title="Marketplace"
                >
                  <FaStore size={20} />
                  <span className={styles.stripNavLabel}>Marketplace</span>
                </NavLink>
              )}
            </nav>
          </div>
          )}
          <div
            ref={setLeftSlotEl}
            className={`${styles.leftInsetSlot} ${
              leftSlotOccupied ? styles.leftInsetSlotActive : ""
            }`}
          />

          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.mobileSearchToggle}
              aria-label="Search"
              title="Search"
              onClick={() => setMobileSearchOpen(true)}
            >
              <FaSearch size={18} />
            </button>
            <button
              type="button"
              className={styles.mobileSearchBack}
              aria-label="Close search"
              onClick={() => setMobileSearchOpen(false)}
            >
              <FaArrowLeft size={18} />
            </button>
            <div className={styles.searchField}>
              <SearchBar compact autoFocus={mobileSearchOpen} />
            </div>
          </div>

          <div className={styles.rightActions}>
            {!userAuthChecked ? (
              <span className={styles.authPending} aria-hidden="true" />
            ) : !user ? (
            <Link to="/login?mode=login" className={styles.guestLoginLink}>
              Log in
            </Link>
          ) : (
            <>
              <NavLink
                to="/users/search"
                end
                className={({ isActive }) =>
                  `${styles.notificationIcon} ${
                    isActive ? styles.notificationIconActive : ""
                  }`
                }
                aria-label="Find people"
                title="Find people"
              >
                <FaUserFriends size={26} />
              </NavLink>

              {/* Messages Icon */}
              <div className={styles.iconWrapper} ref={messagesDropdownRef}>
                <button
                  className={styles.notificationIcon}
                  onClick={handleMessagesClick}
                  type="button"
                  aria-label={
                    unreadMessagesCount > 0
                      ? `${unreadMessagesCount} unread messages`
                      : "Messages"
                  }
                  title={
                    unreadMessagesCount > 0
                      ? `${unreadMessagesCount} unread message${
                          unreadMessagesCount > 1 ? "s" : ""
                        }`
                      : "Messages"
                  }
                >
                  <AiFillMessage size={26} />
                  {unreadMessagesCount > 0 && (
                    <span className={styles.notificationBadge}>
                      {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                    </span>
                  )}
                </button>

                {/* Messages Dropdown */}
                {showMessagesDropdown && (
                  <div className={styles.notificationDropdown}>
                    <div className={styles.dropdownHeader}>
                      <h3>Messages</h3>
                      <Link
                        to={messagesPath}
                        onClick={() => setShowMessagesDropdown(false)}
                        className={styles.viewAllLink}
                      >
                        View all
                      </Link>
                    </div>
                    <div className={styles.dropdownContent}>
                      {loadingMessages ? (
                        <Loader inline={true} />
                      ) : messages.length === 0 ? (
                        <div className={styles.dropdownEmpty}>
                          No new messages
                        </div>
                      ) : (
                        messages.map((m) => {
                          const id = m._id || m.id;
                          const isSystem = Boolean(
                            m.isSystem || m.systemMessage,
                          );
                          return (
                            <div
                              key={id}
                              className={`${styles.dropdownItem} ${
                                !m.read ? styles.unread : ""
                              }`}
                              onClick={(e) => handleOpenMessage(m, e)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOpenMessage(m, e);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className={styles.itemContent}>
                                <div className={styles.itemTitle}>
                                  {m.title}
                                </div>
                                {m.message && (
                                  <div className={styles.itemMessage}>
                                    {m.message.length > 60
                                      ? `${m.message.substring(0, 60)}...`
                                      : m.message}
                                  </div>
                                )}
                                <div className={styles.itemTime}>
                                  {formatTime(m.createdAt || m.date)}
                                </div>
                              </div>
                              <div className={styles.itemActions}>
                                {!m.read && (
                                  <button
                                    className={styles.actionBtn}
                                    onClick={(e) => handleMarkReadMessage(m, e)}
                                    type="button"
                                    aria-label="Mark message as read"
                                    title="Mark as read"
                                  >
                                    <FaCheck />
                                  </button>
                                )}
                                {!isSystem && (
                                  <button
                                    className={styles.actionBtn}
                                    onClick={(e) => handleDeleteMessage(m, e)}
                                    type="button"
                                    aria-label="Delete message notification"
                                    title="Delete"
                                  >
                                    <FaTimes />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications Icon */}
              <div className={styles.iconWrapper} ref={dropdownRef}>
                <button
                  className={styles.notificationIcon}
                  onClick={handleBellClick}
                  type="button"
                  aria-label={
                    unreadCount > 0
                      ? `${unreadCount} unread notifications`
                      : "Notifications"
                  }
                  title={
                    unreadCount > 0
                      ? `${unreadCount} unread notification${
                          unreadCount > 1 ? "s" : ""
                        }`
                      : "Notifications"
                  }
                >
                  <FaBell size={26} />
                  {unreadCount > 0 && (
                    <span className={styles.notificationBadge}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showDropdown && (
                  <div className={styles.notificationDropdown}>
                    <div className={styles.dropdownHeader}>
                      <h3>Notifications</h3>
                      <Link
                        to={notificationsPath}
                        onClick={() => setShowDropdown(false)}
                        className={styles.viewAllLink}
                      >
                        View all
                      </Link>
                    </div>
                    <div className={styles.dropdownContent}>
                      {loadingNotifications ? (
                        <Loader inline={true} />
                      ) : notifications.length === 0 ? (
                        <div className={styles.dropdownEmpty}>
                          No notifications
                        </div>
                      ) : (
                        notifications.map((n) => {
                          const id = n._id || n.id;
                          const isFollowRequest = isFollowRequestNotification(n);
                          const followRequestId = isFollowRequest
                            ? resolveFollowRequestId(n, followRequests)
                            : null;
                          const isAccepting =
                            followRequestId &&
                            acceptingRequestId === followRequestId;

                          return (
                            <div
                              key={id}
                              className={`${styles.dropdownItem} ${
                                !n.read ? styles.unread : ""
                              }`}
                              onClick={(e) => handleOpenNotification(n, e)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOpenNotification(n, e);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className={styles.itemContent}>
                                <div className={styles.itemTitle}>
                                  {n.title}
                                </div>
                                {n.message && (
                                  <div className={styles.itemMessage}>
                                    {n.message.length > 60
                                      ? `${n.message.substring(0, 60)}...`
                                      : n.message}
                                  </div>
                                )}
                                {followRequestId && (
                                  <button
                                    type="button"
                                    className={styles.acceptFollowBtn}
                                    onClick={(e) =>
                                      handleAcceptFollowRequest(
                                        followRequestId,
                                        e,
                                      )
                                    }
                                    disabled={isAccepting}
                                    aria-label="Accept follow request"
                                  >
                                    <FaCheck aria-hidden />
                                    {isAccepting ? "Accepting..." : "Accept"}
                                  </button>
                                )}
                                <div className={styles.itemTime}>
                                  {formatTime(n.createdAt || n.date)}
                                </div>
                              </div>
                              <div className={styles.itemActions}>
                                {!n.read && (
                                  <button
                                    className={styles.actionBtn}
                                    onClick={(e) => handleMarkRead(id, e)}
                                    type="button"
                                    aria-label="Mark notification as read"
                                    title="Mark as read"
                                  >
                                    <FaCheck />
                                  </button>
                                )}
                                <button
                                  className={styles.actionBtn}
                                  onClick={(e) => handleDelete(id, e)}
                                  type="button"
                                  aria-label="Delete notification"
                                  title="Delete"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div
                className={styles.profileMenuWrapper}
                ref={profileMenuWrapperRef}
                onMouseEnter={openProfileMenu}
                onMouseLeave={closeProfileMenu}
              >
                <button
                  className={styles.profileIcon}
                  type="button"
                  onClick={() => navigate(userProfilePath)}
                  aria-label="Open profile"
                  title="Profile"
                >
                  {user?.avatar ? (
                    <img
                      src={getImageUrl(user.avatar)}
                      alt="Profile"
                      className={styles.profileAvatar}
                    />
                  ) : (
                    <FaUserCircle size={36} />
                  )}
                </button>

                {showProfileMenu && (
                  <div className={styles.profileDropdown}>
                    <button
                      type="button"
                      className={styles.profileMenuItem}
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate(userProfilePath);
                      }}
                    >
                      View profile
                    </button>
                    <button
                      type="button"
                      className={styles.profileMenuItem}
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate(settingsPath);
                      }}
                    >
                      Settings
                    </button>
                    <div
                      className={styles.themeSwitchRow}
                      role="group"
                      aria-label="Theme"
                    >
                      <span className={styles.themeSwitchLabel}>Theme</span>
                      <div className={styles.themeSwitch}>
                        {THEME_OPTIONS.map(({ value, label, Icon }) => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.themeSwitchOption}${
                              theme === value
                                ? ` ${styles.themeSwitchOptionActive}`
                                : ""
                            }`}
                            aria-pressed={theme === value}
                            aria-label={`${label} theme`}
                            title={label}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setTheme(value);
                            }}
                          >
                            <Icon aria-hidden />
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.profileMenuItemLogout}
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
